import {
  RFI_BANNER_BLOCK,
  RFI_BANNER_PNG_BASE64,
  RFI_CONTENT_TYPES,
  RFI_DIVIDER_PARAGRAPH,
  RFI_DOCUMENT_HEAD,
  RFI_DOCUMENT_RELS,
  RFI_ENDNOTES,
  RFI_FONT_TABLE,
  RFI_FOOTER,
  RFI_FOOTNOTES,
  RFI_NUMBERING,
  RFI_ROOT_RELS,
  RFI_SECT_PR,
  RFI_SETTINGS,
  RFI_STYLES,
  RFI_THEME,
  RFI_WEB_SETTINGS,
} from "./rfi-template-parts.ts";

export interface RfiItem {
  heading: string;
  issue: readonly string[];
  proposal: readonly string[];
}

export interface RfiDocumentInput {
  projectLine: string;
  to: string;
  from: string;
  issueDate: string;
  signature: string;
  items: readonly RfiItem[];
  specSheetsIncluded: boolean;
}

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

const SPEC_SHEETS_SENTENCE =
  "All equipment requested for substitution has a specification sheet that has been included within the RFI.";

const NOTE_PARAGRAPH =
  '<w:p><w:pPr><w:pStyle w:val="BodyText"/><w:spacing w:before="57"/><w:ind w:left="1440" w:right="1657" w:hanging="1"/></w:pPr>' +
  '<w:r><w:rPr><w:b/></w:rPr><w:t xml:space="preserve">Note: </w:t></w:r>' +
  '<w:r><w:t xml:space="preserve">Responses above is not an authorization to proceed with any work involving additional cost and/or time. If any response from above requires change to the Issued Contract Documents, a Change Order Directive will need to be issued in accordance with the Contract Documents.</w:t></w:r></w:p>';

const NAME_DATE_COMPANY_PARAGRAPH =
  '<w:p><w:pPr><w:tabs><w:tab w:val="left" w:pos="5103"/><w:tab w:val="left" w:pos="7401"/><w:tab w:val="left" w:pos="10834"/></w:tabs><w:spacing w:before="1"/><w:ind w:left="1439"/><w:rPr><w:rFonts w:ascii="Times New Roman"/></w:rPr></w:pPr>' +
  '<w:r><w:rPr><w:b/></w:rPr><w:t>Name:</w:t></w:r>' +
  '<w:r><w:rPr><w:b/><w:u w:val="thick"/></w:rPr><w:t xml:space="preserve"> </w:t></w:r>' +
  '<w:r><w:rPr><w:b/><w:u w:val="thick"/></w:rPr><w:tab/></w:r>' +
  '<w:r><w:rPr><w:b/></w:rPr><w:t>Date:</w:t></w:r>' +
  '<w:r><w:rPr><w:b/><w:u w:val="thick"/></w:rPr><w:t xml:space="preserve"> </w:t></w:r>' +
  '<w:r><w:rPr><w:b/><w:u w:val="thick"/></w:rPr><w:tab/></w:r>' +
  '<w:r><w:rPr><w:b/></w:rPr><w:t>Company:</w:t></w:r>' +
  '<w:r><w:rPr><w:rFonts w:ascii="Times New Roman"/><w:spacing w:val="-8"/></w:rPr><w:t xml:space="preserve"> </w:t></w:r>' +
  '<w:r><w:rPr><w:rFonts w:ascii="Times New Roman"/><w:u w:val="thick"/></w:rPr><w:t xml:space="preserve"> </w:t></w:r>' +
  '<w:r><w:rPr><w:rFonts w:ascii="Times New Roman"/><w:u w:val="thick"/></w:rPr><w:tab/></w:r></w:p>';

function emptyBodyText(rPr = "", spacing = ""): string {
  return `<w:p><w:pPr><w:pStyle w:val="BodyText"/>${spacing}${rPr}</w:pPr></w:p>`;
}

const BOLD_EMPTY = emptyBodyText("<w:rPr><w:b/></w:rPr>");

function escapeXml(text: string): string {
  return text
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, " ")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function textRuns(text: string, rPr: string): string {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const wrapped = rPr === "" ? "" : `<w:rPr>${rPr}</w:rPr>`;
  return lines
    .map((line, i) => {
      const br = i > 0 ? `<w:br/>` : "";
      return `<w:r>${wrapped}${br}<w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r>`;
    })
    .join("");
}

const NOT_BOLD = '<w:b w:val="0"/><w:bCs w:val="0"/>';

function titleParagraph(projectLine: string): string {
  const rPr = '<w:b/><w:bCs/><w:sz w:val="36"/><w:szCs w:val="36"/>';
  return (
    `<w:p><w:pPr><w:spacing w:before="27"/><w:ind w:left="1439"/><w:rPr>${rPr}</w:rPr></w:pPr>` +
    textRuns(projectLine, rPr) +
    "</w:p>"
  );
}

function toFromParagraph(to: string, from: string): string {
  const fromName = from.trim();
  const fromRuns = fromName
    ? `<w:r><w:t xml:space="preserve"> </w:t></w:r>${textRuns(fromName, "")}${textRuns(", Audio Enhancement", '<w:spacing w:val="-4"/>')}`
    : `<w:r><w:t xml:space="preserve"> </w:t></w:r>${textRuns("Audio Enhancement", '<w:spacing w:val="-4"/>')}`;
  return (
    '<w:p><w:pPr><w:pStyle w:val="BodyText"/><w:tabs><w:tab w:val="left" w:pos="6479"/></w:tabs><w:spacing w:before="20"/><w:ind w:left="1439"/></w:pPr>' +
    textRuns("TO: ", "<w:b/><w:bCs/>") +
    textRuns(to, "") +
    "<w:r><w:tab/></w:r>" +
    textRuns("From", '<w:rFonts w:asciiTheme="minorHAnsi" w:hAnsiTheme="minorHAnsi" w:cstheme="minorBidi"/><w:b/><w:bCs/><w:shd w:val="clear" w:color="auto" w:fill="FFFFFF"/>') +
    textRuns(":", "<w:b/><w:bCs/>") +
    fromRuns +
    "</w:p>"
  );
}

function issueDateParagraph(issueDate: string): string {
  return (
    '<w:p><w:pPr><w:ind w:left="6479"/></w:pPr>' +
    textRuns("Issue Date:", "<w:b/><w:bCs/>") +
    textRuns(" " + issueDate, "") +
    "</w:p>" +
    '<w:p><w:pPr><w:ind w:left="6479"/></w:pPr></w:p>'
  );
}

function listParagraph(level: 0 | 1 | 2, text: string): string {
  const rPr = level === 0 ? "" : NOT_BOLD;
  const pRPr = rPr === "" ? "" : `<w:rPr>${rPr}</w:rPr>`;
  return (
    `<w:p><w:pPr><w:pStyle w:val="Heading1"/><w:numPr><w:ilvl w:val="${level}"/><w:numId w:val="3"/></w:numPr>${pRPr}</w:pPr>` +
    textRuns(text, rPr) +
    "</w:p>"
  );
}

function itemParagraphs(item: RfiItem): string {
  return (
    listParagraph(0, item.heading) +
    item.issue.map((t) => listParagraph(1, t)).join("") +
    item.proposal.map((t) => listParagraph(2, t)).join("")
  );
}

function bodyTextParagraph(text: string): string {
  return `<w:p><w:pPr><w:pStyle w:val="BodyText"/></w:pPr>${textRuns(text, "")}</w:p>`;
}

function tabbedParagraph(tabs: number, text: string): string {
  const tabRuns = "<w:r><w:tab/></w:r>".repeat(tabs);
  return `<w:p><w:pPr><w:pStyle w:val="BodyText"/></w:pPr>${tabRuns}${text ? textRuns(text, "") : ""}</w:p>`;
}

export function buildRfiDocumentXml(input: RfiDocumentInput): string {
  const closing = [
    tabbedParagraph(1, ""),
    emptyBodyText(),
    tabbedParagraph(2, "Respectfully,"),
    tabbedParagraph(2, input.signature),
    emptyBodyText("<w:rPr><w:sz w:val=\"21\"/></w:rPr>", '<w:spacing w:before="11"/>'),
  ].join("");

  return (
    RFI_DOCUMENT_HEAD +
    RFI_BANNER_BLOCK +
    titleParagraph(input.projectLine) +
    RFI_DIVIDER_PARAGRAPH +
    toFromParagraph(input.to, input.from) +
    issueDateParagraph(input.issueDate) +
    emptyBodyText() +
    '<w:p><w:pPr><w:pStyle w:val="Heading1"/></w:pPr><w:r><w:t>RFI Description:</w:t></w:r></w:p>' +
    '<w:p><w:pPr><w:pStyle w:val="Heading1"/></w:pPr></w:p>' +
    input.items.map(itemParagraphs).join("") +
    (input.specSheetsIncluded ? bodyTextParagraph(SPEC_SHEETS_SENTENCE) : "") +
    closing +
    '<w:p><w:pPr><w:pStyle w:val="Heading1"/><w:spacing w:before="0"/></w:pPr><w:r><w:t>Receiver’s Response:</w:t></w:r></w:p>' +
    BOLD_EMPTY.repeat(4) +
    emptyBodyText().repeat(3) +
    BOLD_EMPTY.repeat(4) +
    emptyBodyText("<w:rPr><w:b/></w:rPr>", '<w:spacing w:before="1"/>') +
    NAME_DATE_COMPANY_PARAGRAPH +
    emptyBodyText('<w:rPr><w:rFonts w:ascii="Times New Roman"/><w:sz w:val="18"/></w:rPr>', '<w:spacing w:before="5"/>') +
    NOTE_PARAGRAPH +
    RFI_SECT_PR +
    "</w:body></w:document>"
  );
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]!) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const DOS_DATE_1980_01_01 = 0x0021;

export function zipStore(entries: readonly ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const named = entries.map((e) => ({ ...e, nameBytes: encoder.encode(e.name), crc: crc32(e.data) }));
  const localSize = named.reduce((n, e) => n + 30 + e.nameBytes.length + e.data.length, 0);
  const centralSize = named.reduce((n, e) => n + 46 + e.nameBytes.length, 0);
  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);
  let pos = 0;
  const offsets: number[] = [];

  for (const e of named) {
    offsets.push(pos);
    view.setUint32(pos, 0x04034b50, true);
    view.setUint16(pos + 4, 20, true);
    view.setUint16(pos + 6, 0x0800, true);
    view.setUint16(pos + 8, 0, true);
    view.setUint16(pos + 10, 0, true);
    view.setUint16(pos + 12, DOS_DATE_1980_01_01, true);
    view.setUint32(pos + 14, e.crc, true);
    view.setUint32(pos + 18, e.data.length, true);
    view.setUint32(pos + 22, e.data.length, true);
    view.setUint16(pos + 26, e.nameBytes.length, true);
    view.setUint16(pos + 28, 0, true);
    out.set(e.nameBytes, pos + 30);
    out.set(e.data, pos + 30 + e.nameBytes.length);
    pos += 30 + e.nameBytes.length + e.data.length;
  }

  const centralStart = pos;
  named.forEach((e, i) => {
    view.setUint32(pos, 0x02014b50, true);
    view.setUint16(pos + 4, 20, true);
    view.setUint16(pos + 6, 20, true);
    view.setUint16(pos + 8, 0x0800, true);
    view.setUint16(pos + 10, 0, true);
    view.setUint16(pos + 12, 0, true);
    view.setUint16(pos + 14, DOS_DATE_1980_01_01, true);
    view.setUint32(pos + 16, e.crc, true);
    view.setUint32(pos + 20, e.data.length, true);
    view.setUint32(pos + 24, e.data.length, true);
    view.setUint16(pos + 28, e.nameBytes.length, true);
    view.setUint32(pos + 42, offsets[i]!, true);
    out.set(e.nameBytes, pos + 46);
    pos += 46 + e.nameBytes.length;
  });

  view.setUint32(pos, 0x06054b50, true);
  view.setUint16(pos + 8, named.length, true);
  view.setUint16(pos + 10, named.length, true);
  view.setUint32(pos + 12, centralSize, true);
  view.setUint32(pos + 16, centralStart, true);
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export async function buildRfiDocx(input: RfiDocumentInput): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const text = (name: string, xml: string): ZipEntry => ({ name, data: encoder.encode(xml) });
  return zipStore([
    text("[Content_Types].xml", RFI_CONTENT_TYPES),
    text("_rels/.rels", RFI_ROOT_RELS),
    text("word/document.xml", buildRfiDocumentXml(input)),
    text("word/_rels/document.xml.rels", RFI_DOCUMENT_RELS),
    text("word/styles.xml", RFI_STYLES),
    text("word/numbering.xml", RFI_NUMBERING),
    text("word/settings.xml", RFI_SETTINGS),
    text("word/fontTable.xml", RFI_FONT_TABLE),
    text("word/webSettings.xml", RFI_WEB_SETTINGS),
    text("word/theme/theme1.xml", RFI_THEME),
    text("word/footer1.xml", RFI_FOOTER),
    text("word/footnotes.xml", RFI_FOOTNOTES),
    text("word/endnotes.xml", RFI_ENDNOTES),
    { name: "word/media/image1.png", data: base64ToBytes(RFI_BANNER_PNG_BASE64) },
  ]);
}

export function rfiFileName(projectLine: string): string {
  const safe = projectLine
    .replace(/[\u0000-\u001F<>:"/\\|?*]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120)
    .replace(/[. ]+$/, "");
  return safe ? `${safe} RFI.docx` : "RFI.docx";
}
